/**
 * Turns Denker answers in text only, with a soft cue for its voice
 * (Preferences › Voice › "Stay quiet over music, videos and calls", off by
 * default): any turn while another app plays sound - music, a video - or
 * uses the microphone - a call or a meeting. The app asks the system
 * (`denker_sound_elsewhere`) when a prompt is sent - a turn asked over music
 * stays quiet, though the music is paused by it - and again as the turn's
 * events arrive, so music it started quiets its answer too. Denker's own
 * sound and listening never count. Speech of no turn (a voice preview) and
 * guided narration the user asked for still speak.
 */
import { invoke } from "@tauri-apps/api/core";
import { isTauri } from "@/lib/is-tauri";
import { playQuietTurnCue } from "@/lib/notification-sound";
import { useTtsSettingsStore } from "@/stores/tts-settings-store";
import { isContinuousCaptureAlive } from "./continuous-voice-store";
import { getWindowRoleSync } from "./use-window-role";
import { useVoiceAttentionStore } from "./voice-attention-store";

/** What other apps do with sound now (`sound_elsewhere.rs`). */
type SoundElsewhere = { playing: boolean; microphone: boolean };

/** Turns remembered at most: older ones have long been answered. */
const MAX_TURNS = 256;
/** How often a turn's events ask the system again. */
const CHECK_MS = 1_500;
/** An answer older than this says nothing: the music or the call may have ended. */
const FRESH_MS = 10_000;

/** Turns asked while another app played sound or had the microphone. */
const turnsOverSound = new Set<string>();
const cuedTurns = new Set<string>();
let latest: SoundElsewhere = { playing: false, microphone: false };
/** Which ask answered last: an answer to an older ask never replaces a newer one. */
let asked = 0;
let answered = 0;
let checkedAt = 0;
let answeredAt = 0;

function remember(turns: Set<string>, executionId: string): void {
  turns.add(executionId);
  while (turns.size > MAX_TURNS) turns.delete(turns.values().next().value!);
}

const busy = (sound: SoundElsewhere) => sound.playing || sound.microphone;

/** Only the window that speaks asks (`use-tts.ts`), with the setting on and replies read aloud. */
function asks(): boolean {
  const { enabled, quietWhenHeard } = useTtsSettingsStore.getState();
  return (
    isTauri() && getWindowRoleSync() === "owner" && enabled && quietWhenHeard
  );
}

function askSystem(now: number): Promise<SoundElsewhere | undefined> {
  checkedAt = now;
  const ask = ++asked;
  const ownCapture =
    isContinuousCaptureAlive() ||
    useVoiceAttentionStore.getState().recordingId !== null;
  return invoke<SoundElsewhere>("denker_sound_elsewhere", { ownCapture })
    .then((sound) => {
      const now = {
        playing: sound?.playing === true,
        microphone: sound?.microphone === true,
      };
      if (ask > answered) {
        answered = ask;
        latest = now;
        answeredAt = Date.now();
      }
      return now;
    })
    .catch(() => undefined);
}

/**
 * A prompt went into `executionId` - a new turn, or one still running: what
 * quieted it before is forgotten, and the system is asked at once, before
 * its acknowledgement or first sentence would speak.
 */
export function startQuietTurnCheck(
  executionId: string,
  now = Date.now(),
): void {
  turnsOverSound.delete(executionId);
  cuedTurns.delete(executionId);
  if (!asks()) return;
  void askSystem(now).then((sound) => {
    if (sound && busy(sound)) remember(turnsOverSound, executionId);
  });
}

/** A turn's events arrive: the system is asked again, at most every `CHECK_MS`. */
export function refreshSoundElsewhere(now = Date.now()): void {
  if (!asks() || now - checkedAt < CHECK_MS) return;
  void askSystem(now);
}

/** Whether this turn's speech is left out, the setting being on. */
export function isQuietTurn(executionId: string | undefined): boolean {
  if (!useTtsSettingsStore.getState().quietWhenHeard) return false;
  const now = busy(latest) && Date.now() - answeredAt < FRESH_MS;
  return now || (executionId !== undefined && turnsOverSound.has(executionId));
}

/**
 * A turn's own speech left out - never speech of no turn (a voice preview in
 * Settings), nor guided narration the user asked for (`presentation:`). A
 * question left out still says it waits for the user: by the cue.
 */
export function quietForTurn(options?: {
  executionId?: string;
  ttsSource?: string;
}): boolean {
  const { executionId, ttsSource } = options ?? {};
  if (
    !executionId ||
    ttsSource?.startsWith("presentation:") ||
    !isQuietTurn(executionId)
  )
    return false;
  if (ttsSource?.startsWith("awaiting-input:")) cueQuietTurn(executionId);
  return true;
}

/** The soft cue that stands for a quiet turn's answer, once per turn. */
export function cueQuietTurn(executionId: string): void {
  if (cuedTurns.has(executionId)) return;
  remember(cuedTurns, executionId);
  playQuietTurnCue();
}

export function resetQuietTurnsForTests(): void {
  turnsOverSound.clear();
  cuedTurns.clear();
  latest = { playing: false, microphone: false };
  checkedAt = 0;
  answeredAt = 0;
  asked = 0;
  answered = 0;
}

export function setSoundElsewhereForTests(sound: SoundElsewhere): void {
  latest = sound;
  answeredAt = Date.now();
}
